import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";

import { Heading } from "components/Heading";
import { CustomButton } from "components/CustomButton";
import { CustomModal } from "components/CustomModal";
import TimePicker from "components/TimePicker/TimePicker";
import { PageHeadings, PageTitles } from "../../constants";
import {
  getDemoAvailabilitySettings,
  saveDemoAvailabilitySettings,
  getDemoAvailabilityMonth,
  setDemoDateBlocked,
  clearDemoDateSlots,
  addDemoTimeSlot,
  updateDemoTimeSlot,
  deleteDemoTimeSlot,
} from "api/api.service";
import "./Styles.scss";

/**
 * Book-a-Demo availability — real backend, no local dummy data.
 *
 * Every read/write here goes to Neo-backend's /admin/demo-availability/*
 * endpoints. A date with no admin override isn't "unavailable" — it's
 * whatever DemoAvailabilitySettings' recurring weekly template says, and the
 * server (not this component) resolves that, so this screen and the public
 * /portal/book-a-demo calendar (MindCircuit, via the matching
 * /public/demo-availability endpoint) can never disagree about what's open.
 * A slot's `id` is null when it's still on the template rather than a real
 * row — this component treats that exactly like a real slot for editing
 * purposes; the first edit is what materializes it server-side.
 */

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DOW = ["S", "M", "T", "W", "T", "F", "S"];
const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function pad(n) {
  return String(n).padStart(2, "0");
}

function toDateKey(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function buildMonthCells(year, month) {
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) {
    const date = new Date(year, month, d);
    cells.push({ date, dateKey: toDateKey(date) });
  }
  return cells;
}

// available: has >=1 open slot. fully-booked: has slots, none open. blocked:
// admin blocked the whole day. none: nothing bookable at all (a non-working
// day on the template, with no override).
function getDayStatus(day) {
  if (!day) return "none";
  if (day.blocked) return "blocked";
  if (!day.slots.length) return "none";
  const hasAvailable = day.slots.some((s) => s.status === "AVAILABLE");
  return hasAvailable ? "available" : "fully-booked";
}

function countSlots(day) {
  const counts = { available: 0, booked: 0, blocked: 0 };
  (day?.slots || []).forEach((s) => {
    if (s.status === "AVAILABLE") counts.available += 1;
    else if (s.status === "BOOKED") counts.booked += 1;
    else if (s.status === "BLOCKED") counts.blocked += 1;
  });
  return counts;
}

// Stable per-slot React key: a template slot has id=null, so its time is the
// only thing that identifies it within a single day's list.
function slotKey(s) {
  return s.id != null ? `id-${s.id}` : `tpl-${s.startTime}`;
}

const emptySlotDraft = { id: null, start: "", end: "" };

export default function DemoAvailability() {
  const today = useMemo(() => new Date(), []);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDateKey, setSelectedDateKey] = useState(toDateKey(today));

  const [daysByDate, setDaysByDate] = useState({});
  const [monthLoading, setMonthLoading] = useState(true);
  const [mutating, setMutating] = useState(false);

  const [settingsLoading, setSettingsLoading] = useState(true);
  const [notificationEmail, setNotificationEmail] = useState("");
  const [availableDays, setAvailableDays] = useState({});
  const [workingHours, setWorkingHours] = useState({ start: "", end: "" });

  const [slotDraft, setSlotDraft] = useState(null); // Add/Edit Time Slot modal
  const [bookingDraft, setBookingDraft] = useState(null); // Mark as Booked modal
  const [openSlotMenuKey, setOpenSlotMenuKey] = useState(null);

  const loadMonth = useCallback((year, month) => {
    setMonthLoading(true);
    // API month is 1-indexed (human), JS Date's is 0-indexed — see this
    // file's own note by the API call site in api.service.js.
    getDemoAvailabilityMonth(year, month + 1)
      .then((res) => {
        const map = {};
        (res?.days || []).forEach((d) => {
          map[d.date] = d;
        });
        setDaysByDate(map);
      })
      .catch(() => toast.error("Could not load the calendar."))
      .finally(() => setMonthLoading(false));
  }, []);

  const loadSettings = useCallback(() => {
    setSettingsLoading(true);
    getDemoAvailabilitySettings()
      .then((res) => {
        setNotificationEmail(res?.notificationEmail || "");
        const workingDaySet = new Set(res?.workingDays || []);
        setAvailableDays(
          DAY_NAMES.reduce((acc, name, i) => ({ ...acc, [name]: workingDaySet.has(i) }), {})
        );
        setWorkingHours({ start: res?.startTime || "", end: res?.endTime || "" });
      })
      .catch(() => toast.error("Could not load availability settings."))
      .finally(() => setSettingsLoading(false));
  }, []);

  useEffect(() => {
    loadMonth(viewYear, viewMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewYear, viewMonth]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const selectedDay = daysByDate[selectedDateKey];
  const selectedDate = useMemo(() => {
    const [y, m, d] = selectedDateKey.split("-").map(Number);
    return new Date(y, m - 1, d);
  }, [selectedDateKey]);
  const slotCounts = countSlots(selectedDay);
  const totalSlots = (selectedDay?.slots || []).length;

  function shiftMonth(delta) {
    const next = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
  }

  function selectDate(dateKey) {
    setSelectedDateKey(dateKey);
    setOpenSlotMenuKey(null);
  }

  // Every mutation below returns the freshly-computed day from the server —
  // spliced straight into local state rather than re-fetching the whole
  // month, since the endpoint already did the (materialize + recompute) work.
  function applyDayResult(day) {
    setDaysByDate((prev) => ({ ...prev, [day.date]: day }));
  }

  async function runMutation(promise, successMessage) {
    setMutating(true);
    try {
      const day = await promise;
      applyDayResult(day);
      if (successMessage) toast.success(successMessage);
    } catch (e) {
      toast.error(e?.message || "That didn't save. Please try again.");
    } finally {
      setMutating(false);
    }
  }

  function toggleBlockDate() {
    const willBlock = !selectedDay?.blocked;
    runMutation(
      setDemoDateBlocked(selectedDateKey, willBlock),
      willBlock ? "Date blocked." : "Date unblocked."
    );
  }

  function clearAllSlots() {
    runMutation(clearDemoDateSlots(selectedDateKey), "All slots cleared for this date.");
  }

  function openAddSlot() {
    setSlotDraft({ ...emptySlotDraft });
  }

  function openEditSlot(s) {
    setSlotDraft({ id: s.id, start: s.startTime, end: s.endTime, originalStart: s.startTime, originalEnd: s.endTime });
  }

  // A slot still on the recurring template (id === null) has no real row to
  // PUT/DELETE against yet. setDateBlocked(date, false) is a harmless no-op
  // on an already-unblocked date, but as a side effect it materializes the
  // date — copying the template's slots in as real rows — so we can look up
  // the real id that now exists at this slot's original time and act on
  // that, instead of adding a brand-new slot alongside the old one.
  async function materializeAndFindSlot(s) {
    const day = await setDemoDateBlocked(selectedDateKey, false);
    const match = day.slots.find((x) => x.startTime === s.startTime && x.endTime === s.endTime);
    return { day, slotId: match?.id ?? null };
  }

  async function saveSlotDraft() {
    if (!slotDraft?.start || !slotDraft?.end) return;
    const { id, start, end, originalStart, originalEnd } = slotDraft;
    setMutating(true);
    setSlotDraft(null);
    try {
      let day;
      if (id != null) {
        day = await updateDemoTimeSlot(id, { startTime: start, endTime: end });
      } else if (originalStart) {
        // Editing a template slot: materialize, then edit the real row that
        // landed at its original time — not the same as adding a new slot.
        const { slotId } = await materializeAndFindSlot({ startTime: originalStart, endTime: originalEnd });
        if (slotId == null) throw new Error("Could not find that slot after updating this date.");
        day = await updateDemoTimeSlot(slotId, { startTime: start, endTime: end });
      } else {
        day = await addDemoTimeSlot(selectedDateKey, { startTime: start, endTime: end });
      }
      applyDayResult(day);
      toast.success(id != null || originalStart ? "Time slot updated." : "Time slot added.");
    } catch (e) {
      toast.error(e?.message || "That didn't save. Please try again.");
    } finally {
      setMutating(false);
    }
  }

  async function setSlotStatus(s, status) {
    setOpenSlotMenuKey(null);
    setMutating(true);
    try {
      let targetId = s.id;
      if (targetId == null) {
        const result = await materializeAndFindSlot(s);
        targetId = result.slotId;
        if (targetId == null) throw new Error("Could not find that slot after updating this date.");
      }
      const day = await updateDemoTimeSlot(targetId, { status });
      applyDayResult(day);
      toast.success(status === "AVAILABLE" ? "Marked available." : "Slot blocked.");
    } catch (e) {
      toast.error(e?.message || "That didn't save. Please try again.");
    } finally {
      setMutating(false);
    }
  }

  async function openBookSlot(s) {
    setOpenSlotMenuKey(null);
    if (s.id != null) {
      setBookingDraft({ id: s.id, name: "", school: "" });
      return;
    }
    setMutating(true);
    try {
      const { day, slotId } = await materializeAndFindSlot(s);
      applyDayResult(day);
      if (slotId == null) throw new Error("Could not find that slot after updating this date.");
      setBookingDraft({ id: slotId, name: "", school: "" });
    } catch (e) {
      toast.error(e?.message || "That didn't save. Please try again.");
    } finally {
      setMutating(false);
    }
  }

  function saveBookingDraft() {
    if (!bookingDraft?.name.trim()) return;
    runMutation(
      updateDemoTimeSlot(bookingDraft.id, {
        status: "BOOKED",
        bookedByName: bookingDraft.name.trim(),
        bookedBySchool: bookingDraft.school.trim() || undefined,
      }),
      "Slot marked as booked."
    );
    setBookingDraft(null);
  }

  async function deleteSlot(s) {
    setOpenSlotMenuKey(null);
    setMutating(true);
    try {
      let targetId = s.id;
      if (targetId == null) {
        const result = await materializeAndFindSlot(s);
        targetId = result.slotId;
        if (targetId == null) throw new Error("Could not find that slot after updating this date.");
      }
      await deleteDemoTimeSlot(targetId);
      setDaysByDate((prev) => {
        const day = prev[selectedDateKey];
        if (!day) return prev;
        return { ...prev, [selectedDateKey]: { ...day, slots: day.slots.filter((x) => x.id !== targetId) } };
      });
      toast.success("Time slot removed.");
    } catch (e) {
      toast.error(e?.message || "Could not remove that slot.");
    } finally {
      setMutating(false);
    }
  }

  function saveNotificationEmail() {
    if (!notificationEmail.trim()) return;
    saveDemoAvailabilitySettings({ notificationEmail: notificationEmail.trim() })
      .then(() => toast.success("Notification email saved."))
      .catch((e) => toast.error(e?.message || "Could not save that email."));
  }

  function saveWorkingHoursAndDays() {
    const workingDays = DAY_NAMES.reduce((acc, name, i) => (availableDays[name] ? [...acc, i] : acc), []);
    saveDemoAvailabilitySettings({
      workingDays,
      startTime: workingHours.start,
      endTime: workingHours.end,
    })
      .then(() => {
        toast.success("Working hours saved.");
        // The recurring template just changed — refresh the visible month so
        // any date still on the template (no override) reflects it.
        loadMonth(viewYear, viewMonth);
      })
      .catch((e) => toast.error(e?.message || "Could not save working hours."));
  }

  const cells = useMemo(() => buildMonthCells(viewYear, viewMonth), [viewYear, viewMonth]);

  return (
    <div className="page-content demo-availability-page">
      <Helmet>
        <title>{PageTitles.DEMO_AVAILABILITY}</title>
      </Helmet>
      <Container fluid>
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
          <div>
            <Heading heading={PageHeadings.DEMO_AVAILABILITY} />
            <p className="text-muted mb-0">
              Manage the dates and time slots available for demo bookings.
            </p>
          </div>

          <div className="d-flex flex-wrap align-items-end gap-3">
            <Card className="mb-0 da-email-card">
              <CardBody className="py-2 px-3">
                <label className="form-label font-size-13 fw-semibold mb-1">Notification Email</label>
                <p className="text-muted font-size-12 mb-2">
                  Demo booking requests will be sent to this email address.
                </p>
                <div className="d-flex gap-2">
                  <Input
                    type="email"
                    bsSize="sm"
                    value={notificationEmail}
                    onChange={(e) => setNotificationEmail(e.target.value)}
                    placeholder="you@example.com"
                    disabled={settingsLoading}
                  />
                  <CustomButton title="Save" color="primary" onClick={saveNotificationEmail} disabled={settingsLoading} />
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        <Row>
          {/* Calendar */}
          <Col xl={4} lg={6} className="mb-3">
            <Card className="h-100 mb-0">
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h5 className="mb-0 font-family-lexend standard-typo-14-500-ld">
                    {MONTH_NAMES[viewMonth]} {viewYear}
                  </h5>
                  <div className="d-flex gap-1">
                    <button type="button" className="da-cal-arrow" aria-label="Previous month" onClick={() => shiftMonth(-1)}>
                      ‹
                    </button>
                    <button type="button" className="da-cal-arrow" aria-label="Next month" onClick={() => shiftMonth(1)}>
                      ›
                    </button>
                  </div>
                </div>

                <div className={`da-cal-grid${monthLoading ? " is-loading" : ""}`}>
                  {DOW.map((d, i) => (
                    <div className="da-cal-dow" key={`dow-${i}`}>{d}</div>
                  ))}
                  {cells.map((cell, i) => {
                    if (!cell) return <div className="da-cal-cell blank" key={`blank-${i}`} />;
                    const day = daysByDate[cell.dateKey];
                    const status = getDayStatus(day);
                    const isSelected = cell.dateKey === selectedDateKey;
                    const isToday = cell.dateKey === toDateKey(today);
                    return (
                      <button
                        type="button"
                        key={cell.dateKey}
                        className={`da-cal-cell${isSelected ? " selected" : ""}${isToday ? " today" : ""}`}
                        onClick={() => selectDate(cell.dateKey)}
                      >
                        <span className="da-cal-cell-num">{cell.date.getDate()}</span>
                        {status !== "none" && <span className={`da-cal-dot ${status}`} />}
                      </button>
                    );
                  })}
                </div>

                <div className="da-cal-legend">
                  <span><i className="da-cal-dot available" /> Available</span>
                  <span><i className="da-cal-dot fully-booked" /> Fully booked</span>
                  <span><i className="da-cal-dot blocked" /> Blocked</span>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Time slots */}
          <Col xl={5} lg={6} className="mb-3">
            <Card className="h-100 mb-0">
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <h5 className="mb-0 font-family-lexend standard-typo-14-500-ld">
                    {DAY_NAMES[selectedDate.getDay()]}, {MONTH_NAMES[selectedDate.getMonth()]} {selectedDate.getDate()}, {selectedDate.getFullYear()}
                  </h5>
                  <span className="da-badge da-badge-neutral">{totalSlots} slot{totalSlots === 1 ? "" : "s"}</span>
                </div>

                {monthLoading ? (
                  <p className="text-muted font-size-13 mt-3 mb-0">Loading…</p>
                ) : selectedDay?.blocked ? (
                  <div className="da-blocked-banner mt-3">
                    <p className="mb-2">This date is blocked — no bookings are accepted.</p>
                    <CustomButton title="Unblock Date" color="light" onClick={toggleBlockDate} disabled={mutating} />
                  </div>
                ) : (
                  <>
                    <div className="da-slot-list mt-3">
                      {totalSlots === 0 && (
                        <p className="text-muted font-size-13 mb-0">No time slots configured for this date yet.</p>
                      )}
                      {(selectedDay?.slots || []).map((s) => {
                        const key = slotKey(s);
                        return (
                          <div className="da-slot-row" key={key}>
                            <div className="da-slot-time">{s.startTime} – {s.endTime}</div>
                            <div className="da-slot-status">
                              <span className={`da-badge da-badge-${s.status.toLowerCase()}`}>
                                {s.status === "AVAILABLE" ? "Available" : s.status === "BOOKED" ? "Booked" : "Blocked"}
                              </span>
                              {s.status === "BOOKED" && s.bookedByName && (
                                <div className="text-muted font-size-12 mt-1">
                                  {s.bookedByName}
                                  {s.bookedBySchool ? <br /> : null}
                                  {s.bookedBySchool}
                                </div>
                              )}
                            </div>
                            <div className="da-slot-actions">
                              <button type="button" className="da-icon-btn" title="Edit" onClick={() => openEditSlot(s)}>
                                ✎
                              </button>
                              <Dropdown
                                isOpen={openSlotMenuKey === key}
                                toggle={() => setOpenSlotMenuKey(openSlotMenuKey === key ? null : key)}
                              >
                                <DropdownToggle tag="button" className="da-icon-btn" title="More">
                                  ⋮
                                </DropdownToggle>
                                <DropdownMenu end>
                                  {s.status !== "AVAILABLE" && (
                                    <DropdownItem onClick={() => setSlotStatus(s, "AVAILABLE")}>Mark as Available</DropdownItem>
                                  )}
                                  {s.status !== "BOOKED" && (
                                    <DropdownItem onClick={() => openBookSlot(s)}>Mark as Booked</DropdownItem>
                                  )}
                                  {s.status !== "BLOCKED" && (
                                    <DropdownItem onClick={() => setSlotStatus(s, "BLOCKED")}>Mark as Blocked</DropdownItem>
                                  )}
                                  <DropdownItem divider />
                                  <DropdownItem className="text-danger" onClick={() => deleteSlot(s)}>Delete Slot</DropdownItem>
                                </DropdownMenu>
                              </Dropdown>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="d-flex flex-wrap gap-2 mt-3">
                      <CustomButton title="+ Add Time Slot" color="primary" outline onClick={openAddSlot} disabled={mutating} />
                      <CustomButton title="Block Date" color="danger" outline onClick={toggleBlockDate} disabled={mutating} />
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Day summary + quick actions */}
          <Col xl={3} lg={12} className="mb-3">
            <Card className="h-100 mb-0">
              <CardBody>
                <h5 className="mb-1 font-family-lexend standard-typo-14-500-ld">Day Summary</h5>
                <p className="text-muted font-size-12 mb-3">
                  Overview for {MONTH_NAMES[selectedDate.getMonth()]} {selectedDate.getDate()}, {selectedDate.getFullYear()}
                </p>

                <div className="da-summary-stat available">
                  <div className="da-summary-num">{selectedDay?.blocked ? 0 : slotCounts.available}</div>
                  <div className="da-summary-label">Available slots</div>
                </div>
                <div className="da-summary-stat booked">
                  <div className="da-summary-num">{slotCounts.booked}</div>
                  <div className="da-summary-label">Booked demos</div>
                </div>
                <div className="da-summary-stat blocked">
                  <div className="da-summary-num">{selectedDay?.blocked ? totalSlots || "All" : slotCounts.blocked}</div>
                  <div className="da-summary-label">Blocked slots</div>
                </div>

                <p className="font-size-13 fw-semibold mt-4 mb-2">Quick Actions</p>
                <div className="d-flex flex-column gap-2">
                  <CustomButton title="Clear All Slots" color="light" className="w-100" onClick={clearAllSlots} disabled={mutating || monthLoading} />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          {/* Working hours & availability */}
          <Col xl={12} className="mb-3">
            <Card className="h-100 mb-0">
              <CardBody>
                <h5 className="mb-1 font-family-lexend standard-typo-14-500-ld">Working Hours &amp; Availability</h5>
                <p className="text-muted font-size-12 mb-4">
                  Set your regular availability for demo calls. You can override specific dates from the calendar.
                </p>

                <Row>
                  <Col md={7}>
                    <p className="font-size-13 fw-semibold mb-2">Available Days</p>
                    <div className="da-days-grid">
                      {DAY_NAMES.map((day) => (
                        <label className="da-day-checkbox" key={day}>
                          <input
                            type="checkbox"
                            checked={!!availableDays[day]}
                            disabled={settingsLoading}
                            onChange={(e) => setAvailableDays((prev) => ({ ...prev, [day]: e.target.checked }))}
                          />
                          <span>{day}</span>
                        </label>
                      ))}
                    </div>
                  </Col>
                  <Col md={5}>
                    <p className="font-size-13 fw-semibold mb-2">Working Hours</p>
                    <Row>
                      <Col xs={6}>
                        <label className="form-label font-size-12 text-muted mb-1">Start Time</label>
                        <TimePicker
                          value={workingHours.start}
                          onChange={(val) => setWorkingHours((prev) => ({ ...prev, start: val }))}
                          disabled={settingsLoading}
                        />
                      </Col>
                      <Col xs={6}>
                        <label className="form-label font-size-12 text-muted mb-1">End Time</label>
                        <TimePicker
                          value={workingHours.end}
                          onChange={(val) => setWorkingHours((prev) => ({ ...prev, end: val }))}
                          disabled={settingsLoading}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <div className="d-flex justify-content-end mt-3">
                  <CustomButton title="Save Working Hours" color="primary" onClick={saveWorkingHoursAndDays} disabled={settingsLoading} />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <CustomModal isOpen={!!slotDraft} onClose={() => setSlotDraft(null)} toggle={() => setSlotDraft(null)} size="sm">
        {slotDraft && (
          <div className="px-3 pb-3">
            <h5 className="mb-3">{slotDraft.id ? "Edit Time Slot" : "Add Time Slot"}</h5>
            <Row>
              <Col xs={6}>
                <label className="form-label font-size-13">Start Time</label>
                <TimePicker value={slotDraft.start} onChange={(val) => setSlotDraft((p) => ({ ...p, start: val }))} />
              </Col>
              <Col xs={6}>
                <label className="form-label font-size-13">End Time</label>
                <TimePicker value={slotDraft.end} onChange={(val) => setSlotDraft((p) => ({ ...p, end: val }))} />
              </Col>
            </Row>
            <div className="d-flex justify-content-end gap-2 mt-4">
              <CustomButton title="Cancel" color="light" onClick={() => setSlotDraft(null)} />
              <CustomButton
                title="Save"
                color="primary"
                disabled={!slotDraft.start || !slotDraft.end}
                onClick={saveSlotDraft}
              />
            </div>
          </div>
        )}
      </CustomModal>

      <CustomModal isOpen={!!bookingDraft} onClose={() => setBookingDraft(null)} toggle={() => setBookingDraft(null)} size="sm">
        {bookingDraft && (
          <div className="px-3 pb-3">
            <h5 className="mb-3">Mark as Booked</h5>
            <label className="form-label font-size-13">Booked by</label>
            <input
              className="form-control mb-3"
              placeholder="Full name"
              value={bookingDraft.name}
              onChange={(e) => setBookingDraft((p) => ({ ...p, name: e.target.value }))}
            />
            <label className="form-label font-size-13">School / District</label>
            <input
              className="form-control"
              placeholder="Optional"
              value={bookingDraft.school}
              onChange={(e) => setBookingDraft((p) => ({ ...p, school: e.target.value }))}
            />
            <div className="d-flex justify-content-end gap-2 mt-4">
              <CustomButton title="Cancel" color="light" onClick={() => setBookingDraft(null)} />
              <CustomButton title="Save" color="primary" disabled={!bookingDraft.name.trim()} onClick={saveBookingDraft} />
            </div>
          </div>
        )}
      </CustomModal>
    </div>
  );
}
